// Utility functions for generating color palettes from a base color

/**
 * Convert hex color to HSL
 */
function hexToHsl(hex: string): [number, number, number] {
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number;
  let s: number;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
}

/**
 * Convert HSL to hex
 */
function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Convert hex color to rgba with alpha
 */
export function withAlpha(color: string, opacity: number): string {
  // Remove # if present
  const hex = color.replace('#', '');

  // Parse RGB values
  const r = Number.parseInt(hex.substring(0, 2), 16);
  const g = Number.parseInt(hex.substring(2, 4), 16);
  const b = Number.parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Add semantic names to numeric palette
 * NOTE: All original numeric keys (25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950)
 * are preserved via spread operator. Semantic names are ADDED for convenience, not replacing numeric access.
 */
function addSemanticNames<T extends Record<string | number, string>>(palette: T) {
  return {
    ...palette, // All numeric keys preserved: [25], [50], [100], [200], [300], [400], [500], [600], [700], [800], [900], [950]
    // Semantic names added for commonly used values (convenience shortcuts)
    lighter: palette[50] || palette['50'], // Alias for [50]
    light: palette[200] || palette['200'], // Alias for [200]
    main: palette[500] || palette['500'], // Alias for [500]
    dark: palette[600] || palette['600'], // Alias for [600]
    darker: palette[700] || palette['700'], // Alias for [700]
    contrastText: '#ffffff', // Can be enhanced later to calculate based on contrast
  };
}

/**
 * Generate a complete color palette from a base color with semantic names
 */
export function generateColorPalette(baseColor: string) {
  const [h, s, l] = hexToHsl(baseColor);

  const numericPalette = {
    25: hslToHex(h, Math.max(0, s - 20), Math.min(100, l + 45)),
    50: hslToHex(h, Math.max(0, s - 15), Math.min(100, l + 40)),
    100: hslToHex(h, Math.max(0, s - 10), Math.min(100, l + 30)),
    200: hslToHex(h, Math.max(0, s - 5), Math.min(100, l + 20)),
    300: hslToHex(h, s, Math.min(100, l + 10)),
    400: hslToHex(h, Math.min(100, s + 5), l),
    500: baseColor, // The base color
    600: hslToHex(h, Math.min(100, s + 10), Math.max(0, l - 10)),
    700: hslToHex(h, Math.min(100, s + 15), Math.max(0, l - 20)),
    800: hslToHex(h, Math.min(100, s + 20), Math.max(0, l - 30)),
    900: hslToHex(h, Math.min(100, s + 25), Math.max(0, l - 40)),
    950: hslToHex(h, Math.min(100, s + 30), Math.max(0, l - 50)),
  };

  return addSemanticNames(numericPalette);
}

/**
 * Predefined color palettes for common colors with semantic names
 */
export const COLOR_PALETTES = {
  blue: addSemanticNames({
    25: '#f8fafc',
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  }),
  green: addSemanticNames({
    25: '#f7fef9',
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#10b981',
    600: '#16a34a',
    700: '#059669',
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  }),
  red: addSemanticNames({
    25: '#fffbfa',
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  }),
  yellow: addSemanticNames({
    25: '#fffcf5',
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  }),
  purple: addSemanticNames({
    25: '#faf8ff',
    50: '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    300: '#c4b5fd',
    400: '#a78bfa',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
    800: '#5b21b6',
    900: '#4c1d95',
    950: '#2e1065',
  }),
  pink: addSemanticNames({
    25: '#fef7f7',
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#be185d',
    800: '#9d174d',
    900: '#831843',
    950: '#500724',
  }),
  indigo: addSemanticNames({
    25: '#f8fafc',
    50: '#f0f9ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
    950: '#1e1b4b',
  }),
  teal: addSemanticNames({
    25: '#f6fefc',
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
    950: '#042f2e',
  }),
};

/**
 * Get color palette by name or generate from custom color
 */
export function getColorPalette(colorKey: string, customColor?: string) {
  if (customColor) {
    return generateColorPalette(customColor);
  }

  return COLOR_PALETTES[colorKey as keyof typeof COLOR_PALETTES] || COLOR_PALETTES.blue;
}

/**
 * Generate secondary color (complementary hue shift)
 */
export function generateSecondaryColor(primaryColor: string): string {
  const [h, s, l] = hexToHsl(primaryColor);
  // Shift hue by 30 degrees for harmonious secondary
  const secondaryHue = (h + 30) % 360;
  return hslToHex(secondaryHue, Math.max(0, s - 10), Math.max(0, l - 5));
}

/**
 * Generate tertiary color (triadic hue shift)
 */
export function generateTertiaryColor(primaryColor: string): string {
  const [h, s, l] = hexToHsl(primaryColor);
  // Shift hue by 120 degrees for triadic harmony
  const tertiaryHue = (h + 120) % 360;
  return hslToHex(tertiaryHue, Math.max(0, s - 15), Math.max(0, l - 10));
}

/**
 * Complete color system generator
 */
export function generateColorSystem(primaryColorKey: string) {
  const primaryPalette = getColorPalette(primaryColorKey);
  const primaryColor = primaryPalette[500];

  const secondaryColor = generateSecondaryColor(primaryColor);
  const tertiaryColor = generateTertiaryColor(primaryColor);

  return {
    primary: primaryPalette,
    secondary: generateColorPalette(secondaryColor),
    tertiary: generateColorPalette(tertiaryColor),
    // Neutral colors for text and backgrounds
    neutral: {
      25: '#fafafa',
      50: '#f5f5f5',
      100: '#e5e5e5',
      200: '#d4d4d4',
      300: '#a3a3a3',
      400: '#737373',
      500: '#525252',
      600: '#404040',
      700: '#262626',
      800: '#171717',
      900: '#0a0a0a',
      950: '#000000',
    },
  };
}

/**
 * Text color utilities for proper contrast
 */
export function getTextColors(theme: 'light' | 'dark') {
  if (theme === 'dark') {
    return {
      primary: 'rgba(255, 255, 255, 0.85)', // Primary text with slight transparency
      secondary: 'rgba(255, 255, 255, 0.65)', // Secondary text
      tertiary: 'rgba(255, 255, 255, 0.45)', // Tertiary text
      disabled: 'rgba(255, 255, 255, 0.25)', // Disabled text
    };
  }

  return {
    primary: 'rgba(0, 0, 0, 0.85)', // Primary text
    secondary: 'rgba(0, 0, 0, 0.65)', // Secondary text
    tertiary: 'rgba(0, 0, 0, 0.45)', // Tertiary text
    disabled: 'rgba(0, 0, 0, 0.25)', // Disabled text
  };
}

/**
 * Border color utilities - semantic tokens that auto-adapt to theme
 */
export function getBorderColors(theme: 'light' | 'dark') {
  if (theme === 'dark') {
    // Borders slightly lighter than elevated background (#1f1f1f) but not too bright
    // Using rgba values that provide subtle but visible separation
    return {
      primary: 'rgba(255, 255, 255, 0.08)', // Main borders - visible but subtle on #1f1f1f background
      secondary: 'rgba(255, 255, 255, 0.06)', // Subtle borders
      tertiary: 'rgba(255, 255, 255, 0.04)', // Very subtle borders
    };
  }

  return {
    primary: '#f0f0f0', // Main borders - matches standard divider color
    secondary: '#e8e8e8', // Subtle borders
    tertiary: '#f5f5f5', // Very subtle borders
  };
}

/**
 * Surface/background color utilities - semantic tokens that auto-adapt to theme
 */
export function getSurfaceColors(theme: 'light' | 'dark') {
  if (theme === 'dark') {
    return {
      container: '#1f1f1f', // Card/container backgrounds - matches Ant Design darkAlgorithm
      layout: '#141414', // Page/layout backgrounds - matches Ant Design darkAlgorithm default
      elevated: '#1f1f1f', // Elevated surfaces (sidebar, header, modals) - matches Ant Design darkAlgorithm
      hover: '#262626', // Hover states - slightly lighter than container
    };
  }

  return {
    container: '#fcfcfd', // gray[25] - card/container backgrounds
    layout: '#fafafa', // Very light gray - page/layout backgrounds
    elevated: '#ffffff', // white - elevated surfaces (sidebar, header, modals, dropdowns)
    hover: '#f8fafc', // gray[50] - hover states
  };
}

/**
 * Scrollbar colors - theme-aware; no scrollbar tokens in Ant Design, so we provide our own.
 * Applied via CSS variables to ::-webkit-scrollbar and scrollbar-color.
 */
export function getScrollbarColors(theme: 'light' | 'dark') {
  if (theme === 'dark') {
    return {
      track: '#262626', // Dark grey, aligns with surface.hover
      thumb: '#525252', // Medium grey, visible on dark track
      thumbHover: '#737373', // Lighter on hover
    };
  }
  return {
    track: '#f5f5f5', // Light grey
    thumb: '#bfbfbf', // Medium grey, visible on light track
    thumbHover: '#999999', // Darker on hover
  };
}

/**
 * Menu state color utilities - computed from primary color, auto-adapts to theme
 * These tokens define hover, selected, and active indicator (left bar) for menu items
 */
export function getMenuStateColors(
  primaryMain: string,
  primaryLighter: string,
  theme: 'light' | 'dark',
  primaryDark: string,
) {
  if (theme === 'dark') {
    return {
      hover: withAlpha(primaryMain, 0.1), // Subtle primary tint on hover
      selected: withAlpha(primaryMain, 0.12), // Soft primary tint when selected (softer than Ant's solid #1677ff)
      selectedBar: primaryMain, // Left-edge indicator for active item (themeable)
      selectedColor: primaryMain, // Icon and text: use primary (accent) so selected stands out from white unselected, same logic as light
    };
  }

  return {
    hover: primaryLighter, // Primary lighter shade on hover
    selected: primaryLighter, // Primary lighter shade when selected
    selectedBar: primaryMain, // Left-edge indicator for active item (themeable)
    selectedColor: primaryDark, // Icon and text on selected item in light
  };
}

/**
 * Get font family based on customization setting
 * This function should be used with TYPOGRAPHY imported from constants/design
 * Example: getFontFamilyFromCustomization(customization.fontFamily, TYPOGRAPHY)
 */
export function getFontFamilyFromCustomization(
  fontFamily: 'default' | 'serif' | 'monospace',
  typography: { fontFamily: { primary: string; serif: string; mono: string } },
) {
  switch (fontFamily) {
    case 'serif':
      return typography.fontFamily.serif;
    case 'monospace':
      return typography.fontFamily.mono;
    default:
      return typography.fontFamily.primary;
  }
}
